// The Android app's launch URL. The app (a Trusted Web Activity) opens the site with its device data
// in the query string: ?utm_source=twa&brand=…&gaid=…&device_id=…&key=<md5> (format and checks in
// packages/core/src/app-launch.ts). This runs on any page, as early as possible:
//  1. the launch parameters leave the address bar at once (history.replaceState), so the advertising
//     ID and Android ID don't linger in the URL, the history, referrers, screenshots or the offer
//     walls' frames;
//  2. if the integrity key matches, the fields we use are kept in localStorage (gp_app_launch) for
//     lib/appDevice.ts, which sends them to the API once signed in, and again when they change;
//  3. this browser is remembered as the app's (gp_app), for the Account page's Notifications row;
//  4. this tab is marked as the app's, with its build (gp_app_run in sessionStorage), for
//     components/AppUpdateGate.tsx. Per tab, not per browser: the app shares Chrome's storage, so a
//     lasting mark would also stop the same person's ordinary Chrome tabs.
//
// Built as an entry of its own (vite.config.ts, earlyEntries) and loaded async ahead of the app. It
// nearly always runs first; if main.tsx starts before it, main hands over the query in
// window.__gpLaunchSearch, cleans the address bar itself, and imports this file. Nothing imports
// it statically, so its code (and the core module with its MD5) stays in this small file: keep it
// that way, and keep the storage keys below in sync with lib/appDevice.ts.
import {
  APP_PACKAGE,
  extractLaunchData,
  isAppLaunch,
  LAUNCH_KEY_BASES,
  stripLaunchParams,
  verifyLaunchKey,
} from '@gaintplay/core/app-launch';

/** localStorage: { v: 1, at, data: AppLaunchData } from the latest launch whose key matched. */
export const LAUNCH_STORE = 'gp_app_launch';
/** localStorage: "1" once the Android app has opened the site in this browser. */
export const APP_SEEN = 'gp_app';

/** Fired on window once a launch's data is stored, so a signed-in page can send it right away. */
export const LAUNCH_EVENT = 'gp:app-launch';
/** sessionStorage: { build, bridge, version } of the app that opened this tab (0 = unknown). */
export const APP_RUN = 'gp_app_run';
/** Fired on window once APP_RUN is written. */
export const APP_RUN_EVENT = 'gp:app-run';

function capture(): void {
  if (typeof window === 'undefined') return;
  const w = window as Window & { __gpLaunchSearch?: string };
  // Handed over by main.tsx when it started first (and already cleaned the address bar).
  const handed = w.__gpLaunchSearch;
  delete w.__gpLaunchSearch;
  const raw = handed ?? window.location.search;
  const params = new URLSearchParams(raw);
  if (!isAppLaunch(params)) return;
  if (handed === undefined)
    try {
      const { pathname, search, hash } = window.location;
      window.history.replaceState(
        window.history.state,
        '',
        pathname + stripLaunchParams(search) + hash,
      );
    } catch {
      /* a sandboxed frame: nothing to clean */
    }
  try {
    localStorage.setItem(APP_SEEN, '1');
  } catch {
    /* storage blocked */
  }
  // As on the old site: data whose key doesn't match (a cut or mangled URL) is ignored. The app
  // hashes https://gaintplay.com/ today; this site's own origin covers an app that opens it.
  // A link the app opened (a confirmation email, a notification) carries the launch parameters on
  // that page's own URL, so its path is part of what was hashed.
  const here = `${window.location.origin}${window.location.pathname}?`;
  const verified = verifyLaunchKey(
    params,
    [...LAUNCH_KEY_BASES, `${window.location.origin}/?`, here],
    raw,
  );
  const data = extractLaunchData(params);
  // Which app opened this tab. A key that doesn't match still counts when Android says the app
  // opened the page (a Trusted Web Activity's referrer): an old build must not slip past the
  // update page because its URL was mangled. A typed ?utm_source=twa in a browser has neither.
  if (verified || document.referrer.startsWith(`android-app://${APP_PACKAGE}`))
    try {
      sessionStorage.setItem(
        APP_RUN,
        JSON.stringify({
          build: Number(data.appBuild) || 0,
          bridge: Number(data.bridge) || 0,
          version: data.appVersion ?? '',
        }),
      );
      window.dispatchEvent(new Event(APP_RUN_EVENT));
    } catch {
      /* storage blocked */
    }
  if (!verified) return;
  try {
    localStorage.setItem(LAUNCH_STORE, JSON.stringify({ v: 1, at: Date.now(), data }));
  } catch {
    return; // storage blocked: the app sends it again next launch
  }
  window.dispatchEvent(new Event(LAUNCH_EVENT));
}

capture();
